<template>
  <ion-page>
    <ion-content class="welcome-content">
      <div class="background-scene">
        <div class="stars"></div>
      </div>
      
      <div class="profile-settings-header">
        <ion-button fill="clear" class="back-button" @click="goToDashboard">
          <ion-icon :icon="chevronBack"></ion-icon>
          Back
        </ion-button>
        <h1 class="profile-settings-title">{{ isEditing || hasAllFields ? 'Edit Profile' : 'Add New Profile' }}</h1>
      </div>

      <form @submit.prevent="handleSubmit" class="profile-settings-form">
        <div class="form-fields-grid">
          <div class="profile-input-group">
            <ion-label>First Name</ion-label>
            <input 
              type="text" 
              v-model="localFormData.first_name" 
              placeholder="Enter first name"
              class="profile-custom-input"
              required
            />
          </div>

          <div class="profile-input-group">
            <ion-label>Last Name</ion-label>
            <input 
              type="text" 
              v-model="localFormData.last_name" 
              placeholder="Enter last name"
              class="profile-custom-input"
              required
            />
          </div>

          <div class="profile-input-group">
            <ion-label>Email</ion-label>
            <input 
              type="email" 
              v-model="localFormData.email" 
              placeholder="Enter email"
              class="profile-custom-input"
              required
            />
          </div>

          <div class="profile-input-group">
            <ion-label>Phone Number</ion-label>
            <input 
              type="tel" 
              v-model="localFormData.phone" 
              placeholder="Enter phone number"
              class="profile-custom-input"
            />
          </div>

          <div class="profile-input-group profile-select-group">
            <ion-label>Select Profile</ion-label>
            <select 
              id="profile-select" 
              :value="''" 
              @change="handleProfileDropdownChange($event)" 
              class="profile-custom-input"
            >
              <option value="" disabled selected>Select a profile to edit</option>
              <option value="__add_new__">Add New Profile</option>
              <option v-for="profile in profiles" :key="profile.id" :value="profile.id">
                {{ profile.first_name }} {{ profile.last_name }}
              </option>
            </select>
          </div>

          <div class="profile-input-group action-buttons-group">
            <ion-button 
              v-if="lastSelectedProfileId" 
              @click="handleDeleteProfile(lastSelectedProfileId)" 
              class="delete-button"
              style="--background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%) !important; --color: white !important; background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%) !important; border: none !important; box-shadow: 0 4px 15px rgba(255, 107, 107, 0.3) !important;"
            >
              Delete Profile
            </ion-button>
          </div>
        </div>

        <div class="profile-form-actions">
          <ion-button class="cancel-button" @click="goToDashboard">
            Cancel
          </ion-button>
          <ion-button type="submit" class="add-button" @click="handleAddProfile" :disabled="hasMatchingProfile">
            Add Profile
          </ion-button>
          <ion-button type="submit" class="save-button" @click="handleUpdateProfile">
            Save Changes
          </ion-button>
        </div>
      </form>

      <!-- Toast Notification -->
      <div v-if="showToast" class="toast-notification" :class="toastType">
        <div class="toast-content">
          <ion-icon :icon="toastIcon" class="toast-icon"></ion-icon>
          <span class="toast-message">{{ toastMessage }}</span>
        </div>
        <ion-button fill="clear" class="toast-close" @click="hideToast">
          <ion-icon :icon="close"></ion-icon>
        </ion-button>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import { IonPage, IonContent, IonButton, IonLabel, IonIcon } from '@ionic/vue';
import { chevronBack, checkmark, close, alertCircle } from 'ionicons/icons';
import { useProfileStore } from '@/store/profile';
import { mapState, mapActions } from 'pinia';

export default {
  name: 'ProfileSettings',
  components: {
    IonPage,
    IonContent,
    IonButton,
    IonLabel,
    IonIcon
  },
  data() {
    return {
      lastSelectedProfileId: '',
      chevronBack,
      checkmark,
      close,
      alertCircle,
      showToast: false,
      toastMessage: '',
      toastType: 'success',
      toastIcon: checkmark,
      // Local form data to avoid triggering profile store changes
      localFormData: {
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      }
    }
  },
  computed: {
    ...mapState(useProfileStore, ['currentProfile', 'isEditing', 'profiles']),
    hasAllFields() {
      return this.currentProfile && 
             this.currentProfile.first_name && 
             this.currentProfile.last_name && 
             this.currentProfile.email && 
             this.currentProfile.phone;
    },
    hasMatchingProfile() {
      if (!this.profiles || !this.localFormData.first_name || !this.localFormData.last_name || !this.localFormData.email) {
        return false;
      }
      
      // Check if there's already a profile with the same first name, last name, and email
      return this.profiles.some(profile => 
        profile.first_name === this.localFormData.first_name &&
        profile.last_name === this.localFormData.last_name &&
        profile.email === this.localFormData.email
      );
    },
    safeCurrentProfile() {
      return this.currentProfile || {
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      };
    }
  },
  methods: {
    ...mapActions(useProfileStore, [
      'addProfile',
      'updateProfile',
      'deleteProfile',
      'setCurrentProfile',
      'resetCurrentProfile',
      'fetchProfiles'
    ]),
    goToDashboard() {
      this.fetchProfiles();
      this.$router.push('/dashboard');
    },
    showSuccessToast(message) {
      this.toastMessage = message;
      this.toastType = 'success';
      this.toastIcon = this.checkmark;
      this.showToast = true;
      setTimeout(() => {
        this.hideToast();
      }, 4000);
    },
    showErrorToast(message) {
      this.toastMessage = message;
      this.toastType = 'error';
      this.toastIcon = this.alertCircle;
      this.showToast = true;
      setTimeout(() => {
        this.hideToast();
      }, 5000);
    },
    hideToast() {
      this.showToast = false;
    },
    async handleAddProfile() {
      try {
        console.log('=== ADDING NEW PROFILE ===');
        console.log('Attempting to add new profile with data:', {
          firstName: this.localFormData.first_name,
          lastName: this.localFormData.last_name,
          email: this.localFormData.email,
          phone: this.localFormData.phone,
          timestamp: new Date().toISOString()
        });

        // Validation for required fields
        const missingFields = [];
        if (!this.localFormData.first_name) missingFields.push('First Name');
        if (!this.localFormData.last_name) missingFields.push('Last Name');
        if (!this.localFormData.email) missingFields.push('Email');
        if (!this.localFormData.phone) missingFields.push('Phone Number');
        
        if (missingFields.length > 0) {
          this.showErrorToast('Please fill in the following fields: ' + missingFields.join(', '));
          return;
        }

        // Create new profile object from local form data
        const newProfile = {
          first_name: this.localFormData.first_name,
          last_name: this.localFormData.last_name,
          email: this.localFormData.email,
          phone: this.localFormData.phone
        };

        console.log('=== PROFILE ADDITION ATTEMPT ===');
        console.log('Adding profile with data:', {
          firstName: newProfile.first_name,
          lastName: newProfile.last_name,
          email: newProfile.email,
          phone: newProfile.phone,
          timestamp: new Date().toISOString()
        });

        // Add the new profile to the database
        await this.addProfile(newProfile);

        console.log('=== PROFILE ADDITION SUCCESS ===');
        console.log('Successfully added new profile:', {
          firstName: newProfile.first_name,
          lastName: newProfile.last_name,
          email: newProfile.email,
          phone: newProfile.phone,
          timestamp: new Date().toISOString()
        });

        // Refresh the profiles list to include the new profile
        await this.fetchProfiles();

        // Clear the form for next use
        this.localFormData.first_name = '';
        this.localFormData.last_name = '';
        this.localFormData.email = '';
        this.localFormData.phone = '';

        // Reset the last selected profile ID
        this.lastSelectedProfileId = '';

        // Reset the profile dropdown to show placeholder
        const profileSelect = document.getElementById('profile-select');
        if (profileSelect) {
          profileSelect.value = '';
        }

        this.showSuccessToast('New profile added successfully!');
        
        console.log('=== FORM CLEARED FOR NEXT USE ===');
        console.log('Form ready for adding another profile');
        
      } catch (error) {
        console.error('=== PROFILE ADDITION FAILURE ===');
        console.error('Failed to add new profile:', {
          firstName: this.localFormData.first_name,
          lastName: this.localFormData.last_name,
          email: this.localFormData.email,
          phone: this.localFormData.phone,
          error: error.message,
          timestamp: new Date().toISOString()
        });
        console.error('Full error details:', error);
        this.showErrorToast('Error adding new profile. Please try again.');
      }
    },
    async handleUpdateProfile() {
      try {
        if (!this.lastSelectedProfileId) {
          this.showErrorToast('No profile selected for editing. Please select a profile first.');
          return;
        }
        
        // Validation for required fields using local form data
        const missingFields = [];
        if (!this.localFormData.first_name) missingFields.push('First Name');
        if (!this.localFormData.last_name) missingFields.push('Last Name');
        if (!this.localFormData.email) missingFields.push('Email');
        if (!this.localFormData.phone) missingFields.push('Phone Number');
        if (missingFields.length > 0) {
          this.showErrorToast('Please fill in the following fields: ' + missingFields.join(', '));
          return;
        }

        // Create profile object from local form data
        const profileToUpdate = {
          id: this.lastSelectedProfileId,
          first_name: this.localFormData.first_name,
          last_name: this.localFormData.last_name,
          email: this.localFormData.email,
          phone: this.localFormData.phone
        };

        console.log('=== PROFILE UPDATE ATTEMPT ===');
        console.log('Updating profile with data:', {
          profileID: profileToUpdate.id,
          firstName: profileToUpdate.first_name,
          lastName: profileToUpdate.last_name,
          email: profileToUpdate.email,
          phone: profileToUpdate.phone,
          timestamp: new Date().toISOString()
        });

        await this.updateProfile(profileToUpdate);

        console.log('=== PROFILE UPDATE SUCCESS ===');
        console.log('Successfully updated profile:', {
          profileID: profileToUpdate.id,
          firstName: profileToUpdate.first_name,
          lastName: profileToUpdate.last_name,
          email: profileToUpdate.email,
          phone: profileToUpdate.phone,
          timestamp: new Date().toISOString()
        });

        this.showSuccessToast('Profile updated successfully!');
      } catch (error) {
        console.error('=== PROFILE UPDATE FAILURE ===');
        console.error('Failed to update profile:', {
          profileID: this.lastSelectedProfileId,
          firstName: this.localFormData.first_name,
          lastName: this.localFormData.last_name,
          email: this.localFormData.email,
          phone: this.localFormData.phone,
          error: error.message,
          timestamp: new Date().toISOString()
        });
        console.error('Full error details:', error);
        this.showErrorToast('Error updating profile. Please try again.'); 
      }
    },
    handleProfileDropdownChange(event) {
      const selectedId = event.target.value;
      console.log('=== PROFILE DROPDOWN CHANGE ===');
      console.log('Selected ID:', selectedId);
      
      if (selectedId === '__add_new__') {
        console.log('=== SELECTING ADD NEW PROFILE ===');
        console.log('Clearing form fields for new profile entry');
        
        // Clear the local form data directly - no profile store changes
        this.localFormData.first_name = '';
        this.localFormData.last_name = '';
        this.localFormData.email = '';
        this.localFormData.phone = '';
        
        // Reset the last selected profile ID
        this.lastSelectedProfileId = '';
        
        // Reset the profile dropdown to show placeholder
        event.target.value = '';
        
        console.log('=== ADD NEW PROFILE SELECTED ===');
        console.log('Form fields cleared, ready for new data entry');
        console.log('Local form data after clearing:', this.localFormData);
        return;
      }
      
      const selected = this.profiles.find(p => p.id == selectedId);
      if (selected) {
        console.log('=== SELECTING EXISTING PROFILE ===');
        console.log('Loading existing profile:', selected.first_name, selected.last_name);
        
        // Load the selected profile into the local form data
        this.localFormData.first_name = selected.first_name || '';
        this.localFormData.last_name = selected.last_name || '';
        this.localFormData.email = selected.email || '';
        this.localFormData.phone = selected.phone || '';
        
        // Set the current profile in the store for other components
        this.setCurrentProfile(selected);
        this.lastSelectedProfileId = selectedId;
        
        // Reset the select to always show the placeholder
        event.target.value = '';
        
        console.log('=== EXISTING PROFILE LOADED ===');
        console.log('Profile data loaded into form fields');
        console.log('Local form data after loading:', this.localFormData);
      }
    },
    async handleDeleteProfile(profileID) {
      const confirmed = window.confirm('Are you sure you want to delete this user and all their budget data? This action cannot be undone.');
      if (!confirmed) return;
      
      try {
        // Find the profile being deleted for logging
        const profileToDelete = this.profiles.find(p => p.id == profileID || p._id == profileID);
        
        console.log('=== PROFILE DELETION ATTEMPT ===');
        console.log('Attempting to delete profile:', {
          profileID: profileID,
          firstName: profileToDelete?.first_name || 'Unknown',
          lastName: profileToDelete?.last_name || 'Unknown',
          email: profileToDelete?.email || 'Unknown',
          phone: profileToDelete?.phone || 'Unknown',
          timestamp: new Date().toISOString()
        });

        await this.deleteProfile(profileID);

        console.log('=== PROFILE DELETION SUCCESS ===');
        console.log('Successfully deleted profile:', {
          profileID: profileID,
          firstName: profileToDelete?.first_name || 'Unknown',
          lastName: profileToDelete?.last_name || 'Unknown',
          email: profileToDelete?.email || 'Unknown',
          phone: profileToDelete?.phone || 'Unknown',
          timestamp: new Date().toISOString()
        });

        this.showSuccessToast('Profile deleted successfully!');
        
        // After deletion, automatically select the default profile (logged-in user's profile)
        const userID = localStorage.getItem('userID');
        const defaultProfile = this.profiles.find(p => p.id == userID || p._id == userID);
        
        if (defaultProfile) {
          console.log('=== SELECTING DEFAULT PROFILE AFTER DELETION ===');
          console.log('Default profile found:', defaultProfile.first_name, defaultProfile.last_name);
          
          // Load the default profile into the form
          this.localFormData.first_name = defaultProfile.first_name || '';
          this.localFormData.last_name = defaultProfile.last_name || '';
          this.localFormData.email = defaultProfile.email || '';
          this.localFormData.phone = defaultProfile.phone || '';
          
          // Set as current profile in the store
          this.setCurrentProfile(defaultProfile);
          this.lastSelectedProfileId = defaultProfile.id || defaultProfile._id;
          
          console.log('=== DEFAULT PROFILE LOADED ===');
          console.log('Form now shows default profile data');
        } else {
          console.log('=== NO DEFAULT PROFILE FOUND ===');
          console.log('Clearing form fields as fallback');
          
          // Fallback: clear form if no default profile found
          this.localFormData.first_name = '';
          this.localFormData.last_name = '';
          this.localFormData.email = '';
          this.localFormData.phone = '';
          this.lastSelectedProfileId = '';
        }
        
        // Reset the profile dropdown to show placeholder
        const profileSelect = document.getElementById('profile-select');
        if (profileSelect) {
          profileSelect.value = '';
        }
      } catch (error) {
        console.error('=== PROFILE DELETION FAILURE ===');
        console.error('Failed to delete profile:', {
          profileID: profileID,
          firstName: profileToDelete?.first_name || 'Unknown',
          lastName: profileToDelete?.last_name || 'Unknown',
          email: profileToDelete?.email || 'Unknown',
          phone: profileToDelete?.phone || 'Unknown',
          error: error.message,
          timestamp: new Date().toISOString()
        });
        console.error('Full error details:', error);
        this.showErrorToast('Error deleting profile. Please try again.');
      }
    }
  },
  created() {
    this.fetchProfiles();
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      this.$router.push('/login');
      return;
    }
    
    // Initialize local form data if there's a current profile
    if (this.currentProfile) {
      this.localFormData.first_name = this.currentProfile.first_name || '';
      this.localFormData.last_name = this.currentProfile.last_name || '';
      this.localFormData.email = this.currentProfile.email || '';
      this.localFormData.phone = this.currentProfile.phone || '';
    }
  },
  watch: {
    currentProfile: {
      handler(newProfile) {
        // If currentProfile becomes null (logout), redirect to login
        if (!newProfile) {
          this.$router.push('/login');
        }
      },
      immediate: true
    }
  }
}
</script>